import { useCallback, useMemo, ReactNode } from "react";
import classnames from "classnames";
import { Block as BlockIcon, WarningAmber } from "@mui/icons-material";
import { formatInTimeZone } from "date-fns-tz";

import { useTranslation } from "@/lib/i18n";
import { TimeRangeDimension } from "@/lib/dimensions/time-range-dimension";
import { MapLayerController, MapLayersController } from "@/lib/map-layers";
import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";
import LayerLegendTooltip from "@/features/shared/widgets/map/controls/layer-legend-tooltip";
import LayerToggle from "@/features/shared/widgets/map/controls/layer-toggle";
import { LayersPanelContainer } from "@/features/shared/widgets/map/map.styles";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";

export type LayerStatusOverride = {
  isLoading?: boolean;
  error?: ReactNode;
};

type LayersPanelProps = {
  mapLayersController: MapLayersController;
  onShowLegendForLayer: (layerId: string) => void;
  layerStatusOverrides?: Record<string, LayerStatusOverride>;
};

function LayerRow({
  mapLayerController,
  onShowLegendForLayer,
  statusOverride,
}: {
  mapLayerController: MapLayerController;
  onShowLegendForLayer: (layerId: string) => void;
  statusOverride?: LayerStatusOverride;
}) {
  const timezone = useWidgetTimezone()?.name ?? "UTC";

  const {
    dimensions: layerDimensions,
    isEnabled,
    isReady,
    hasError,
    hasPartialLoadErrors,
    hasLoadErrorAtCurrentTime,
    isActive,
    renderedLegend,
    isLoading,
  } = mapLayerController;
  const layerTimeDimension = useMemo(
    () => layerDimensions?.find((it) => it instanceof TimeRangeDimension),
    [layerDimensions],
  );

  const effectiveIsLoading = statusOverride?.isLoading ?? isLoading;

  const errorElement = useMemo(() => {
    if (statusOverride?.error) {
      return statusOverride.error;
    }

    if (hasError && renderedLegend) {
      return <ErrorTooltip>{renderedLegend}</ErrorTooltip>;
    }

    if (hasLoadErrorAtCurrentTime) {
      return <FailedTimestepTooltip />;
    }

    if (hasPartialLoadErrors && renderedLegend) {
      return <ErrorTooltip>{renderedLegend}</ErrorTooltip>;
    }

    if (isEnabled && isReady && !isActive)
      if (layerTimeDimension) {
        return (
          <DataAvailabilityTooltip
            startTimestamp={layerTimeDimension.start.timestamp}
            endTimestamp={layerTimeDimension.end.timestamp}
            timezone={timezone}
          />
        );
      } else {
        return <EmptyTimeDimensionTooltip />;
      }

    return undefined;
  }, [
    statusOverride?.error,
    hasError,
    hasPartialLoadErrors,
    hasLoadErrorAtCurrentTime,
    renderedLegend,
    isEnabled,
    isReady,
    isActive,
    layerTimeDimension,
    timezone,
  ]);

  const handleShowLegendClick = useCallback(() => {
    onShowLegendForLayer(mapLayerController.id);
  }, [mapLayerController.id, onShowLegendForLayer]);

  return (
    <LayerToggle
      {...mapLayerController}
      isLoading={effectiveIsLoading}
      error={errorElement}
      renderedLegend={renderedLegend}
      onShowLegendClick={
        renderedLegend != null && isEnabled ? handleShowLegendClick : undefined
      }
    />
  );
}

const LayersPanel = ({
  mapLayersController,
  onShowLegendForLayer,
  layerStatusOverrides,
}: LayersPanelProps) => {
  return (
    <LayersPanelContainer className={classnames(panelCommonStyles.frostedPanel)}>
      {mapLayersController.layers.map((mapLayerController) => (
        <LayerRow
          key={mapLayerController.id}
          mapLayerController={mapLayerController}
          onShowLegendForLayer={onShowLegendForLayer}
          statusOverride={layerStatusOverrides?.[mapLayerController.id]}
        />
      ))}
    </LayersPanelContainer>
  );
};

export default LayersPanel;

function ParameterLoadErrorIcon() {
  const { t } = useTranslation();
  return (
    <LayerLegendTooltip
      noWidth
      ariaLabel={t("map.layer_error_info")}
      title={t("map.layer_error_info")}
    >
      <WarningAmber />
    </LayerLegendTooltip>
  );
}

export function stationLayerParameterErrorOverride(): LayerStatusOverride {
  return { error: <ParameterLoadErrorIcon /> };
}

function ErrorTooltip({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <LayerLegendTooltip noWidth ariaLabel={t("map.layer_error_info")} title={children}>
      <WarningAmber />
    </LayerLegendTooltip>
  );
}

function FailedTimestepTooltip() {
  const { t } = useTranslation();
  return (
    <LayerLegendTooltip
      noWidth
      ariaLabel={t("map.layer_timestep_load_error_info")}
      title={t("map.layer_timestep_load_error")}
    >
      <BlockIcon />
    </LayerLegendTooltip>
  );
}

function DataAvailabilityTooltip({
  startTimestamp,
  endTimestamp,
  timezone,
}: {
  startTimestamp: Date;
  endTimestamp: Date;
  timezone: string;
}) {
  const { t } = useTranslation();
  return (
    <LayerLegendTooltip
      ariaLabel={t("map.layer_data_availability_info")}
      title={
        <div>
          Data available between <br />
          <strong>
            {formatInTimeZone(startTimestamp, timezone, "dd-MM-yyyy HH:mm")}
          </strong>{" "}
          - <strong>{formatInTimeZone(endTimestamp, timezone, "dd-MM-yyyy HH:mm")}</strong>
        </div>
      }
    >
      <BlockIcon />
    </LayerLegendTooltip>
  );
}

function EmptyTimeDimensionTooltip() {
  const { t } = useTranslation();
  return (
    <LayerLegendTooltip
      ariaLabel={t("map.layer_no_data_info")}
      title={t("map.layer_no_data_in_selected_period")}
    >
      <BlockIcon />
    </LayerLegendTooltip>
  );
}
